/**
 * WalkthroughVideoCard — drop-in card that renders a tour video on the
 * landlord + tenant dashboards. Video URL comes from env so the user
 * can swap providers (YouTube unlisted, Vimeo, MP4) without a code edit.
 *
 * If no URL is configured the card hides entirely — landlords/tenants
 * see nothing instead of a broken placeholder.
 *
 * Env vars (frontend/.env):
 *   REACT_APP_LANDLORD_WALKTHROUGH_URL   — embed URL (YouTube /embed/<id>, Vimeo player URL, or .mp4)
 *   REACT_APP_LANDLORD_WALKTHROUGH_POSTER — optional poster image
 *   REACT_APP_TENANT_WALKTHROUGH_URL
 *   REACT_APP_TENANT_WALKTHROUGH_POSTER
 */
import { useState } from "react";
import { PlayCircle, X, Sparkles } from "lucide-react";

function isIframeUrl(url) {
  return /youtube\.com|youtu\.be|vimeo\.com|loom\.com/i.test(url || "");
}

function toEmbed(url) {
  if (!url) return "";
  // YouTube short → /embed/<id>; full URL → as-is if already embed
  const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}?rel=0&modestbranding=1`;
  return url;
}

export function WalkthroughVideoCard({ role = "landlord" }) {
  const cfg = role === "tenant"
    ? { url: process.env.REACT_APP_TENANT_WALKTHROUGH_URL, poster: process.env.REACT_APP_TENANT_WALKTHROUGH_POSTER, title: "Tenant tour", subtitle: "How to pay rent, submit maintenance, and chat with your landlord — in 90 seconds." }
    : { url: process.env.REACT_APP_LANDLORD_WALKTHROUGH_URL, poster: process.env.REACT_APP_LANDLORD_WALKTHROUGH_POSTER, title: "Landlord tour", subtitle: "Add a property, invite a tenant, and collect your first rent payment — full walkthrough." };

  const [open, setOpen] = useState(false);

  if (!cfg.url) return null;
  const embed = toEmbed(cfg.url);
  const iframe = isIframeUrl(cfg.url);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`walkthrough-${role}-card`}
        className="w-full text-left rounded-2xl bg-gradient-to-br from-slate-950 via-emerald-900 to-teal-800 hover:from-slate-900 hover:to-teal-700 text-white p-5 sm:p-6 flex items-center gap-4 group transition-colors shadow-lg shadow-emerald-900/15"
      >
        <div className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden flex-none bg-slate-800">
          {cfg.poster ? (
            <img src={cfg.poster} alt="" className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"/>
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-emerald-300"/>
            </div>
          )}
          <div className="absolute inset-0 flex items-center justify-center">
            <PlayCircle className="w-8 h-8 sm:w-10 sm:h-10 text-white drop-shadow-lg group-hover:scale-110 transition-transform"/>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-300">Watch the tour</div>
          <div className="font-display font-bold text-base sm:text-lg mt-1 leading-snug">{cfg.title}</div>
          <p className="text-xs sm:text-sm text-slate-300 mt-1 hidden sm:block max-w-md">{cfg.subtitle}</p>
        </div>
        <div className="hidden sm:block text-xs font-bold text-emerald-300 group-hover:translate-x-0.5 transition-transform">Play →</div>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-8 bg-slate-950/80 backdrop-blur-sm" onClick={() => setOpen(false)} data-testid={`walkthrough-${role}-modal`}>
          <div className="relative w-full max-w-4xl aspect-video rounded-2xl overflow-hidden bg-black shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <button type="button" onClick={() => setOpen(false)} aria-label="Close"
              data-testid={`walkthrough-${role}-close`}
              className="absolute top-3 right-3 z-10 w-10 h-10 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center transition-colors">
              <X className="w-5 h-5"/>
            </button>
            {iframe ? (
              <iframe
                src={embed}
                title={cfg.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="w-full h-full"
              />
            ) : (
              <video
                src={embed}
                controls
                autoPlay
                poster={cfg.poster}
                className="w-full h-full"
              />
            )}
          </div>
        </div>
      )}
    </>
  );
}
